/**
 * Directory Search widget — inline people search for the dashboard.
 *
 * Cross-References:
 *   - FR-DASH-002: Default widgets
 *   - dashboard screen spec: Directory Search widget
 *   - S3.10: User directory search API (GET /users/directory)
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import type { WidgetProps, UserSummary } from '@/types';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

export function DirectorySearchWidget(_props: WidgetProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSummary[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(async (q: string) => {
    if (q.length < MIN_QUERY_LEN) {
      setResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const data = await apiFetch<{ items: UserSummary[] }>(
        `/users/directory?q=${encodeURIComponent(q)}&limit=5`,
      );
      setResults(data.items);
    } catch {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.length >= MIN_QUERY_LEN) {
      timerRef.current = setTimeout(() => search(query), DEBOUNCE_MS);
    } else {
      setResults([]);
    }
    return () => clearTimeout(timerRef.current);
  }, [query, search]);

  function initials(name: string): string {
    return name
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
  }

  return (
    <div className="dir-search-widget">
      <div className="dir-search-widget__input-wrap">
        <Search size={14} className="dir-search-widget__icon" />
        <input
          type="search"
          className="dir-search-widget__input"
          placeholder="Search people…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search directory"
        />
      </div>

      {isSearching && (
        <div className="dir-search-widget__status">Searching…</div>
      )}

      {!isSearching && query.length >= MIN_QUERY_LEN && results.length === 0 && (
        <div className="dir-search-widget__status">No results found.</div>
      )}

      {results.length > 0 && (
        <ul className="dir-search-widget__results" role="listbox">
          {results.map((user) => (
            <li key={user.id} role="option" aria-selected={false}>
              <button
                className="dir-search-widget__result"
                onClick={() => navigate(`/users/${user.id}`)}
              >
                <span className="dir-search-widget__avatar">
                  {initials(user.display_name)}
                </span>
                <span className="dir-search-widget__info">
                  <span className="dir-search-widget__name">{user.display_name}</span>
                  <span className="dir-search-widget__email">{user.email}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
